import { runMoveSkillToTrashWorkflow } from "@/action-dialog";
import { undoRestoreSkillErrorMessage, useRestoreSkill } from "@/api/archived";
import { useDeleteSkill, usePublishSkill, usePutSkill } from "@/api/skills";
import { useAppNavigate } from "@/hooks/pane-routing";
import { useRailRowMultiMenuGate } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailMultiSelectionMenu";
import { RailRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRow";
import { RailRowOpenButton } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowOpenButton";
import {
  useRailRowSelection,
  useRegisterRailResource,
  type RailSelectionSource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionMenu,
  useObjectActions,
  useSkillObjectActionHandlers,
  type ObjectKind,
} from "@/object-actions";
import { useOpenSkill } from "@/panes/skill/useOpenSkill";
import { cn } from "@/utils";
import { RESOURCE_TAG_FAVORITE, type Skill } from "@cohesive/api-client";
import { Icon } from "@cohesive/ui-components";
import { ScrollText } from "lucide-react";
import { useMemo } from "react";
import toast from "react-hot-toast";

const SKILL_KIND: ObjectKind = { type: "skill" };

function SkillEnabledDot({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={cn(
        "ml-1 size-1.5 shrink-0 rounded-full",
        enabled ? "bg-lc-active" : "bg-n-40",
      )}
      aria-hidden
    />
  );
}

export function SkillsRailSkillRow({
  skill,
  active,
  section = "canonical",
  dragRef,
  dragProps,
  isDragging,
}: {
  skill: Skill;
  active?: boolean;
  /** Rail section this row lives in — drives the source-aware highlight. */
  section?: RailSelectionSource;
  dragRef?: (element: HTMLElement | null) => void;
  dragProps?: React.HTMLAttributes<HTMLElement>;
  isDragging?: boolean;
}) {
  const openSkill = useOpenSkill();
  const appNavigate = useAppNavigate();
  const updateSkill = usePutSkill();
  const deleteSkill = useDeleteSkill();
  const restoreSkill = useRestoreSkill();
  const publishSkill = usePublishSkill({
    onSuccess: () => toast.success("Published to your organization"),
    onError: (error) => toast.error(error.message || "Failed to publish skill"),
  });
  const menu = useRailRowContextMenu();

  const isEnabled = skill.status === "enabled";
  const isFavorite = !!skill.tags?.includes(RESOURCE_TAG_FAVORITE);
  const { selected, onActivate, rowKey, selectionAttrs } = useRailRowSelection(
    skill.id,
    section,
    !!active,
    "skill",
  );

  const registered = useMemo(
    () => ({ kind: "skill" as const, data: skill }),
    [skill],
  );
  useRegisterRailResource("skill", skill.id, registered);

  const onContextMenu = useRailRowMultiMenuGate(rowKey, menu.onContextMenu);

  const skillActionDeps = useMemo(
    () => ({
      edit: () => openSkill(skill.id),
      toggleEnabled: () =>
        updateSkill.mutate(
          {
            id: skill.id,
            data: { status: isEnabled ? "disabled" : "enabled" },
          },
          { onError: () => toast.error("Failed to update skill.") },
        ),
      publish: () => publishSkill.mutate(skill.id),
      moveToTrash: () =>
        void runMoveSkillToTrashWorkflow({
          skill,
          deleteSkill,
          restoreSkill,
          onRestoreError: (err) =>
            toast.error(undoRestoreSkillErrorMessage(err)),
          onSuccess: () => {
            if (active) appNavigate({ pane: "rightPane", to: null });
          },
        }),
    }),
    [
      active,
      appNavigate,
      deleteSkill,
      isEnabled,
      openSkill,
      publishSkill,
      restoreSkill,
      skill,
      updateSkill,
    ],
  );

  const handlers = useSkillObjectActionHandlers(skill, skillActionDeps);
  const { actions } = useObjectActions({
    kind: SKILL_KIND,
    surface: "left-rail",
    enabled: menu.open,
    state: { isFavorite, isSkillEnabled: isEnabled },
    handlers,
  });

  return (
    <>
      <div
        ref={dragRef}
        data-rail-skill-row
        {...selectionAttrs}
        onContextMenu={onContextMenu}
        className={cn(
          "group/row relative",
          dragProps && "cursor-grab active:cursor-grabbing",
          isDragging && "opacity-50",
          selected && "bg-foreground/[0.04] text-n-90",
        )}
        {...dragProps}
      >
        <RailRow
          icon={ScrollText}
          label={skill.name}
          active={false}
          className={cn(
            "select-none pr-8",
            selected && "rounded-none hover:bg-transparent",
          )}
          leading={
            skill.icon?.type ? (
              <span className="shrink-0 text-n-50">
                <Icon icon={skill.icon} size={16} />
              </span>
            ) : undefined
          }
          trailing={<SkillEnabledDot enabled={isEnabled} />}
          // Skills have nothing to expand: single click selects, double click
          // opens — one gesture set across the rail and the detail-page grids.
          onClick={(e) => onActivate(e)}
          onDoubleClick={(e) => {
            onActivate(e);
            openSkill(skill.id);
          }}
        />

        {/* Mirrors the double-click gesture: select, then open. Absolute (the
            RailRow is itself a <button>, which can't host a nested button);
            the solid background covers any long label it overlaps. */}
        <RailRowOpenButton
          label={skill.name}
          onOpen={() => {
            onActivate();
            openSkill(skill.id);
          }}
          className="absolute right-8 top-1/2 z-10 -translate-y-1/2"
        />

        {/* Registry-driven (surface `left-rail`). RailRowMenu stays `modal={false}`
            under the hood: the delete action opens the "Move to Trash"
            AlertDialog — a modal dropdown + dialog both lock <body>
            pointer-events and Radix miscounts them, freezing the app. */}
        <ObjectActionMenu
          actions={actions}
          kind={SKILL_KIND}
          name={skill.name}
          menuController={menu}
          label={skill.name}
        />
      </div>
    </>
  );
}
